import { findActiveBranchIndex } from "./schemaToRows";
import { prettify } from "./types";

export type StepScope =
  | "type-selector"
  | "scalar-group"
  | "object"
  | "array-item"
  | "optional-section"
  | "review";

export interface Step {
  id: string;
  label: string;
  rowIdPrefix: string;
  scope: StepScope;
  isOptional: boolean;
  arrayParentPath?: (string | number)[];
  arrayItemIndex?: number;
  arrayAddLabel?: string;
}

function getActiveProperties(schema: any, data: any): Record<string, any> {
  if (Array.isArray(schema?.oneOf)) {
    const idx = findActiveBranchIndex(schema, data);
    return schema.oneOf[idx]?.properties ?? {};
  }
  return schema?.properties ?? {};
}

function isScalarSchema(schema: any): boolean {
  const t = schema?.type;
  if (t === "string" || t === "number" || t === "integer" || t === "boolean") return true;
  if (Array.isArray(t) && t.some((x: string) => x !== "null")) return true;
  if (Array.isArray(schema?.enum)) return true;
  return false;
}

export function buildStepPlan(schema: any, draft: any): Step[] {
  if (!schema?.properties) return [];

  const steps: Step[] = [];
  const props = schema.properties as Record<string, any>;
  const required = new Set<string>(Array.isArray(schema.required) ? schema.required : []);

  const scalarKeys: string[] = [];

  for (const [key, propSchema] of Object.entries(props)) {
    if (key.startsWith("__")) continue;
    if (propSchema?.const !== undefined) continue;
    if (isScalarSchema(propSchema)) {
      scalarKeys.push(key);
      continue;
    }

    if (Array.isArray(propSchema?.oneOf) && propSchema.oneOf.length > 0) {
      if (propSchema.__optionalSection) {
        steps.push({
          id: key,
          label: (propSchema.title as string) || prettify(key),
          rowIdPrefix: key,
          scope: "optional-section",
          isOptional: true,
        });
      } else {
        steps.push({
          id: key,
          label: (propSchema.title as string) || prettify(key),
          rowIdPrefix: key,
          scope: "type-selector",
          isOptional: !required.has(key),
        });
      }
      const activeProps = getActiveProperties(propSchema, draft?.[key]);
      walkObjectIntoSteps(key, activeProps, draft?.[key], steps);
      continue;
    }

    if (propSchema?.type === "array" || Array.isArray(propSchema?.items)) {
      const value = draft?.[key];
      const items: unknown[] = Array.isArray(value) ? value : [];
      const minItems = Math.max(1, propSchema?.minItems ?? (required.has(key) ? 1 : 0));
      const count = Math.max(items.length, minItems);
      const itemLabel = (propSchema?.items?.title as string) || prettify(key.replace(/s$/, ""));
      for (let i = 0; i < count; i += 1) {
        steps.push({
          id: `${key}.${i}`,
          label: `${itemLabel} ${i + 1}`,
          rowIdPrefix: `${key}.${i}`,
          scope: "array-item",
          isOptional: false,
          arrayParentPath: [key],
          arrayItemIndex: i,
          arrayAddLabel: `Add another ${itemLabel}`,
        });
      }
      continue;
    }

    if (propSchema?.type === "object" || propSchema?.properties) {
      steps.push({
        id: key,
        label: (propSchema.title as string) || prettify(key),
        rowIdPrefix: key,
        scope: "object",
        isOptional: !required.has(key),
      });
    }
  }

  if (scalarKeys.length > 0) {
    steps.unshift({
      id: "__identity",
      label: "Identity & Basics",
      rowIdPrefix: "",
      scope: "scalar-group",
      isOptional: false,
    });
  }

  steps.push({
    id: "__review",
    label: "Review",
    rowIdPrefix: "",
    scope: "review",
    isOptional: false,
  });

  return steps;
}

function walkObjectIntoSteps(
  parentKey: string,
  props: Record<string, any>,
  data: any,
  steps: Step[]
) {
  for (const [key, propSchema] of Object.entries(props)) {
    if (key.startsWith("__")) continue;
    if (propSchema?.const !== undefined) continue;
    if (propSchema?.type === "array" || Array.isArray(propSchema?.items)) {
      const items: unknown[] = Array.isArray(data?.[key]) ? data[key] : [];
      const minItems = Math.max(1, propSchema?.minItems ?? 1);
      const count = Math.max(items.length, minItems);
      const itemLabel = (propSchema?.items?.title as string) || prettify(key.replace(/s$/, ""));
      for (let i = 0; i < count; i += 1) {
        steps.push({
          id: `${parentKey}.${key}.${i}`,
          label: `${itemLabel} ${i + 1}`,
          rowIdPrefix: `${parentKey}.${key}.${i}`,
          scope: "array-item",
          isOptional: false,
          arrayParentPath: [parentKey, key],
          arrayItemIndex: i,
          arrayAddLabel: `Add another ${itemLabel}`,
        });
      }
    }
  }
}
